import React, { useState } from 'react';
import { useOtelSpans } from '../../hooks/useOtelSpans';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, Filter, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export const SpansViewer = ({ autoRefresh }) => {
  const [filters, setFilters] = useState({
    vendor: null,
    hours: 24,
    limit: 50,
  });

  const { data, isLoading, isError, refetch } = useOtelSpans(filters, { autoRefresh });

  const getProviderColor = (provider) => {
    const colors = {
      openai: 'bg-green-500',
      anthropic: 'bg-orange-500',
      google: 'bg-blue-500',
    };
    return colors[provider] || 'bg-gray-500';
  };

  const formatCost = (cost) => {
    return `$${cost.toFixed(6)}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              <CardTitle>Filters</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Provider</label>
              <Select
                value={filters.vendor || 'all'}
                onValueChange={(value) => setFilters({ ...filters, vendor: value === 'all' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Providers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Time Range</label>
              <Select
                value={filters.hours.toString()}
                onValueChange={(value) => setFilters({ ...filters, hours: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last Hour</SelectItem>
                  <SelectItem value="6">Last 6 Hours</SelectItem>
                  <SelectItem value="24">Last 24 Hours</SelectItem>
                  <SelectItem value="168">Last Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle>Spans</CardTitle>
          <CardDescription>
            {data?.total || 0} spans found
            {autoRefresh && <span className="ml-2 text-green-600">(Auto-refreshing)</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load spans. Please check your backend connection.
              </AlertDescription>
            </Alert>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && !isError && data?.spans && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.spans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No spans found. Try adjusting your filters or run some LLM calls with auto-instrumentation.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.spans.map((span) => (
                      <TableRow key={span.span_id}>
                        <TableCell>
                          <Badge className={getProviderColor(span.provider)}>
                            {span.provider}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{span.model}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatTimestamp(span.timestamp)}
                        </TableCell>
                        <TableCell className="text-right">{span.duration_ms}ms</TableCell>
                        <TableCell className="text-right">
                          <div className="text-sm">
                            <div>{span.attributes.total_tokens}</div>
                            <div className="text-xs text-muted-foreground">
                              {span.attributes.input_tokens}↑ {span.attributes.output_tokens}↓
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCost(span.cost_usd)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={span.status === 'OK' ? 'success' : 'destructive'}>
                            {span.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
